import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { Lock, LogIn, Loader2, Tv2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { liveApi, setViewerToken, setViewerMeta, getViewerToken } from './liveClient';

export default function LiveLoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [its, setIts] = useState('');
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const params = new URLSearchParams(loc.search);
  const claimToken = params.get('claim');     // when scanning QR from mobile
  const nextPath = params.get('next');        // deep-link to a specific stream after login

  useEffect(() => {
    if (getViewerToken() && !claimToken) nav(nextPath || '/live/dashboard', { replace: true });
  }, [nav, claimToken, nextPath]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!/^\d+$/.test(its.trim())) { toast.error('ITS must be digits'); return; }
    if (!pwd) { toast.error('Password required'); return; }
    setBusy(true);
    try {
      const api = liveApi();
      const r = await api.post('/livestream/viewer/login', { its_id: its.trim(), password: pwd });
      setViewerToken(r.data.token);
      setViewerMeta(r.data.viewer);
      if (claimToken) {
        // Now claim the QR for the TV
        try {
          await api.post(`/livestream/qr/claim/${claimToken}`);
          toast.success('TV authorised — you can return to the TV');
          nav('/live/qr-success', { replace: true });
          return;
        } catch (er) {
          toast.error(er?.response?.data?.detail || 'QR claim failed');
        }
      }
      if (r.data.must_change_password) nav('/live/change-password', { replace: true });
      else nav(nextPath || '/live/dashboard', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden" style={{
      background: 'radial-gradient(ellipse at top, #2a0608 0%, #1a0305 40%, #0a0102 100%)',
    }}>
      <Toaster richColors position="top-center" theme="dark" />

      {/* Ornamental gold pattern overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60'><path d='M30 6 l8 22 22 2 -18 14 8 22 -20-14 -20 14 8-22 -18-14 22-2z' fill='none' stroke='%23d4af37' stroke-width='0.6'/></svg>\")",
        backgroundSize: '60px 60px',
      }} />

      <div className="w-full max-w-md relative">
        {/* Alam with glow */}
        <div className="relative inline-block w-full text-center mb-6">
          <div className="absolute inset-0 blur-3xl opacity-30" style={{ background: 'radial-gradient(circle, rgba(212, 175, 55, 0.7) 0%, transparent 70%)' }} />
          <img src="/live/alam.png" alt="Ya Hussain Alam"
            className="relative w-44 sm:w-52 mx-auto drop-shadow-[0_0_30px_rgba(220,38,38,0.4)]"
            style={{ animation: 'wave 6s ease-in-out infinite' }} />
        </div>

        <div className="text-center mb-6">
          <div className="text-amber-300/90 text-xs tracking-[0.4em] uppercase font-light mb-1">Labbaik Ya Hussain</div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(180deg, #fde68a 0%, #d4af37 50%, #92400e 100%)',
            fontFamily: '"Playfair Display", "Cormorant Garamond", serif',
          }}>Anjuman e Saifee Live</h1>
          <p className="text-amber-200/60 text-sm mt-1">Sign in with your ITS to watch authorised streams</p>
        </div>

        <form onSubmit={submit} className="rounded-xl p-6 space-y-4 shadow-2xl backdrop-blur-sm" style={{
          background: 'linear-gradient(135deg, rgba(40, 8, 10, 0.65) 0%, rgba(20, 4, 6, 0.85) 100%)',
          border: '1px solid rgba(212, 175, 55, 0.25)',
        }}>
          {claimToken && (
            <div className="bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 rounded-md p-3 text-sm flex items-start gap-2">
              <Tv2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Authorise this TV by signing in below. Your TV will start streaming automatically.</span>
            </div>
          )}
          <div>
            <label className="text-xs text-amber-200/80 font-semibold uppercase tracking-wide">ITS ID</label>
            <Input value={its} onChange={(e) => setIts(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="8-digit ITS"
              className="bg-black/40 border-amber-500/30 text-amber-50 placeholder:text-amber-100/30 focus:border-amber-400 focus:ring-amber-400/30"
              data-testid="live-login-its" maxLength={8} autoFocus />
          </div>
          <div>
            <label className="text-xs text-amber-200/80 font-semibold uppercase tracking-wide flex items-center gap-1"><Lock className="w-3 h-3" /> Password</label>
            <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
              placeholder="Default: same as ITS (you'll be asked to change)"
              className="bg-black/40 border-amber-500/30 text-amber-50 placeholder:text-amber-100/30 focus:border-amber-400 focus:ring-amber-400/30"
              data-testid="live-login-pwd" />
          </div>
          <Button type="submit" disabled={busy} className="w-full text-white font-semibold tracking-wide" style={{
            background: 'linear-gradient(135deg, #d4af37 0%, #92400e 100%)',
            border: '1px solid rgba(253, 230, 138, 0.4)',
          }} data-testid="live-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LogIn className="w-4 h-4 mr-1" />}
            Sign In
          </Button>
          <div className="text-center text-xs text-amber-200/50 pt-2 border-t border-amber-500/15">
            Forgot password? Contact admin to reset.
          </div>
        </form>

        <div className="text-center mt-6 text-amber-300/40 text-[10px] tracking-[0.3em] uppercase">Anjuman e Saifee · Nagpur</div>
      </div>

      <style>{`
        @keyframes wave {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50% { transform: rotate(2deg) translateY(-4px); }
        }
      `}</style>
    </div>
  );
}
